import React, { useState, useMemo } from 'react';
import { useCard } from '../../themes/cardStyles';
import { buildPaletteTheme, hpColor } from '../../themes/palettes';
import { CharacterAvatar } from '../primitives/CharacterAvatar';
import { ResourceBar } from '../primitives/ResourceBar';

export const SoloCharCard = ({
  theme,
  density,
  char,
  palette,
  cardStyle,
  cardFinish = 'solid',
  dimmed = false,
  onHoverChange,
  onClick,
  onDelete,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const [hovered, setHovered] = useState(false);

  const tintedTheme = useMemo(
    () => buildPaletteTheme(theme, palette, cardStyle, cardFinish, {
      soloDarken: 4,
      alphaProfile: 'solo',
    }),
    [theme, palette, cardStyle, cardFinish]
  );

  const cardStyleObj = useCard(tintedTheme, cardStyle, hovered);

  // Border alpha varies by card style. Bordered solo cards get a more
  // defined outline (palette color at 0x80 ≈ 50%); glass and elevated
  // (currently identical) get a midweight border (0x60); flat gets a
  // soft border (0x40) since fill carries it.
  const borderAlpha = cardStyle === 'bordered' ? '80'
    : (cardStyle === 'glass' || cardStyle === 'elevated') ? '60'
    : '40';
  const solidBorderStyle = cardFinish === 'solid' ? {
    border: `1px solid ${palette.color}${borderAlpha}`,
  } : {};

  return (
    <div
      onMouseEnter={() => { setHovered(true); onHoverChange && onHoverChange(true); }}
      onMouseLeave={() => { setHovered(false); onHoverChange && onHoverChange(false); }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `${char.name} solo character` : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        ...cardStyleObj,
        ...solidBorderStyle,
        padding: 14,
        cursor: onClick ? 'pointer' : 'default',
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: t.fontFamily,
        position: 'relative',
        overflow: 'hidden',
        // When another solo card in the list is hovered, this one recedes.
        opacity: dimmed ? 0.65 : 1,
        filter: dimmed ? 'saturate(0.8)' : 'none',
        transition: `${e.transition || 'all 200ms ease'}, opacity 180ms ease, filter 180ms ease`,
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: palette.color,
        opacity: hovered ? 1 : 0.5,
      }} />
      {char.isDemo && (
        <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: palette.light,
          background: `${palette.color}22`,
          border: `1px solid ${palette.color}66`,
          fontFamily: t.fontFamily,
        }}>Demo</div>
      )}
      {onDelete && (
        <button
          type="button"
          aria-label={`Delete ${char.name}`}
          onClick={(ev) => {
            ev.stopPropagation();
            onDelete();
          }}
          onKeyDown={(ev) => ev.stopPropagation()}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '4px 8px',
            borderRadius: e.borderRadiusSm || '6px',
            border: `1px solid ${c.danger || '#d85c5c'}88`,
            background: 'rgba(0,0,0,0.45)',
            color: c.danger || '#ff8f8f',
            fontFamily: t.fontFamily,
            fontSize: '0.7rem',
            cursor: 'pointer',
            zIndex: 2,
          }}
        >
          Delete
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 42, height: 42,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.4)',
          border: `2px solid ${palette.color}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: `0 0 12px ${palette.glow}`,
        }}>
          <CharacterAvatar char={char} size={42} color={palette.light} fontFamily={t.headingFamily} />
        </div>
        <div style={{ lineHeight: 1.15 }}>
          <div style={{
            fontFamily: t.headingFamily || 'Georgia, serif',
            color: palette.light,
            fontWeight: 600,
            fontSize: '1.05rem',
            textShadow: `${e.headingTextShadow || ''}, 0 0 18px rgba(0,0,0,0.55), 0 2px 4px rgba(0,0,0,0.6)`,
          }}>{char.name}</div>
          <div style={{ color: c.textMuted, fontSize: '0.74rem' }}>{char.cls}</div>
        </div>
      </div>
      <div style={{ marginBottom: 8, marginTop: 'auto' }}>
        <ResourceBar label="HP" current={char.hp} max={char.maxHp} color={hpColor(char.hp, char.maxHp, theme)} theme={theme} compact />
      </div>
      {(char.isNew || char.lastPlayed) && (
        <div style={{ fontSize: '0.7rem', color: char.isNew ? palette.color : c.textMuted, fontWeight: char.isNew ? 600 : 400 }}>
          {char.isNew ? '✨ never played' : char.lastPlayed}
        </div>
      )}
    </div>
  );
};
